import React from 'react';
import { useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import cx from 'classnames';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { useClient } from '@plone/volto/hooks/client/useClient';
import alertSVG from '@plone/volto/icons/alert.svg';
import checkSVG from '@plone/volto/icons/check.svg';
import infoSVG from '@plone/volto/icons/info.svg';
import clockSVG from '@plone/volto/icons/clock.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';

import {
  dataCompleta,
  descreverNotificacao,
  tempoRelativo,
  tomDaNotificacao,
} from 'volto-ministerio-publico-federal/helpers/Notificacoes';
import EtapasFluxo from './EtapasFluxo';
import SolicitarNacional, {
  usePodeSolicitarNacional,
} from './SolicitarNacional';
import type { Notificacao } from 'volto-ministerio-publico-federal/helpers/Notificacoes';

const ICONES = {
  acao: clockSVG,
  alerta: alertSVG,
  sucesso: checkSVG,
  info: infoSVG,
};

type Props = {
  item: Notificacao;
  onAbrir: (item: Notificacao) => void;
  onAlternarLida: (item: Notificacao) => void;
  onArquivar?: (item: Notificacao) => void;
  /** No painel o comentário vem truncado; na página, inteiro. */
  compacto?: boolean;
};

const NotificacaoItem = ({
  item,
  onAbrir,
  onAlternarLida,
  onArquivar,
  compacto = true,
}: Props) => {
  const isClient = useClient();
  const token = useSelector((state: any) => state.userSession?.token);
  const usuarioAtual: string | null = token
    ? (jwtDecode(token) as any).sub
    : null;
  const tom = tomDaNotificacao(item);
  const destino = flattenToAppURL(item.url);
  const podeSolicitar = usePodeSolicitarNacional(
    item.url,
    item.fluxo === 'noticia' &&
      item.para_estado === 'visible' &&
      item.disponivel,
  );

  return (
    <li
      className={cx(
        'mpf-notificacoes__item',
        `mpf-notificacoes__item--${tom}`,
        {
          'mpf-notificacoes__item--nao-lida': !item.lida,
        },
      )}
    >
      <span className="mpf-notificacoes__icone" aria-hidden="true">
        <Icon name={ICONES[tom]} size="16px" />
      </span>
      <div className="mpf-notificacoes__corpo">
        {item.disponivel ? (
          <UniversalLink
            href={destino}
            className="mpf-notificacoes__titulo"
            onClick={() => onAbrir(item)}
          >
            {item.titulo}
          </UniversalLink>
        ) : (
          <span className="mpf-notificacoes__titulo mpf-notificacoes__titulo--indisponivel">
            {item.titulo} (indisponível)
          </span>
        )}
        <p className="mpf-notificacoes__descricao">
          {descreverNotificacao(item, usuarioAtual)}
          {item.para_estado_titulo && (
            <>
              {' · '}
              <span className="mpf-notificacoes__estado">
                {item.para_estado_titulo}
              </span>
            </>
          )}
        </p>
        <EtapasFluxo
          estado={item.para_estado}
          estadoAnterior={item.de_estado}
          fluxo={item.fluxo}
          compacto={compacto}
          proximaEtapa={
            podeSolicitar
              ? {
                  rotulo: 'Publicação nacional',
                  acao: (
                    <SolicitarNacional
                      url={item.url}
                      titulo={item.titulo}
                      onClick={() => onAbrir(item)}
                    />
                  ),
                }
              : undefined
          }
        />
        {item.comentario && (
          <blockquote
            className={cx('mpf-notificacoes__comentario', {
              'mpf-notificacoes__comentario--compacto': compacto,
            })}
          >
            {item.comentario}
          </blockquote>
        )}
        <div className="mpf-notificacoes__rodape-item">
          {item.categoria === 'acao_necessaria' && !item.lida && (
            <span className="mpf-notificacoes__etiqueta">Requer sua ação</span>
          )}
          <time
            className="mpf-notificacoes__tempo"
            dateTime={item.created}
            title={isClient ? dataCompleta(item.created) : undefined}
          >
            {isClient ? tempoRelativo(item.created, Date.now()) : ''}
          </time>
        </div>
      </div>
      <div className="mpf-notificacoes__acoes">
        <button
          type="button"
          className="mpf-notificacoes__acao"
          onClick={() => onAlternarLida(item)}
          aria-label={
            item.lida
              ? `Marcar "${item.titulo}" como não lida`
              : `Marcar "${item.titulo}" como lida`
          }
          title={item.lida ? 'Marcar como não lida' : 'Marcar como lida'}
        >
          <span
            className={cx('mpf-notificacoes__ponto', {
              'mpf-notificacoes__ponto--lida': item.lida,
            })}
            aria-hidden="true"
          />
        </button>
        {onArquivar && (
          <button
            type="button"
            className="mpf-notificacoes__acao"
            onClick={() => onArquivar(item)}
            aria-label={`Arquivar a notificação "${item.titulo}"`}
            title="Arquivar"
          >
            <Icon name={deleteSVG} size="18px" />
          </button>
        )}
      </div>
    </li>
  );
};

export default NotificacaoItem;
